/**
 * StringTagsField — свободный текст-массив тегов на ui/Input + ui/Badge.
 *
 * Enter или запятая добавляют тег, крестик убирает. В отличие от
 * MultiSelectChips (выбор из фиксированного справочника) это ПРОИЗВОЛЬНЫЙ
 * пользовательский ввод — для «Других названий» (народные/местные синонимы
 * сорняков/болезней/вредителей, AK-reference) и любых free-text string[] полей
 * reference-pattern.
 *
 * Domain-композит поверх ui/* примитивов (как MultiSelectChips /
 * NameTranslationsField) — не новый DS-примитив.
 */
import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export interface StringTagsFieldProps {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** aria-label базы для кнопки удаления (напр. «Убрать синоним»). */
  removeLabel?: string;
}

export function StringTagsField({
  values,
  onChange,
  placeholder,
  disabled,
  removeLabel = 'Убрать',
}: StringTagsFieldProps) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };

  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Добавить"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <Badge key={v} variant="secondary" className="gap-1 pr-1">
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="rounded p-0.5 hover:bg-foreground/10"
                aria-label={`${removeLabel} ${v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default StringTagsField;
